import type { MessageList } from '@/api/message-lists/types'

export type MessageListChannel = 'email' | 'sms' | undefined

// Pull the recipient count for the active channel out of a list's stats
// snapshot. SMS uses the valid-mobile figure (matches the "valid recipients"
// shown elsewhere); email uses the email subscriber count. With no channel
// (e.g. the audience-filter list picker), use totalOptins - the deduped count
// of fans opted-in/reachable on at least one channel - rather than `total`,
// which is raw list membership regardless of consent.
//
// For email, a live send-preview count (see useLiveEmailRecipientCount) wins
// over the snapshot when supplied: the snapshot's email figure counts opted-in
// fans only, but an event's "All attendees" list sends regardless of opt-in, so
// it is lower than who the send reaches (EC-4647).
export function recipientCountForList(
  list: MessageList | null | undefined,
  channel: MessageListChannel,
  liveEmailCount: number | null = null,
): number | null {
  if (channel === 'email' && liveEmailCount !== null) return liveEmailCount
  const stats = list?.statsSnapshot
  if (!stats) return null
  if (channel === 'sms') return stats.sms?.validMobile ?? stats.sms?.total ?? null
  if (channel === 'email') return stats.email ?? null
  return stats.totalOptins ?? stats.total ?? null
}

// How to label a list option. Event/group context resolves to a single
// internal "All attendees" list. Campaign context returns one list per
// audience (all registrants plus any reward tiers), each carrying a friendly
// meta.name that matches the AM2 editor - use it over the raw
// _internal_campaign_..._ name. Everything else shows the list's own name.
export function getListOptionLabel(
  list: MessageList,
  context: { isEventContext: boolean, isCampaignContext: boolean },
): string {
  if (context.isEventContext) return 'All attendees'
  if (context.isCampaignContext) return list.meta?.name || list.name
  return list.name
}

// "No valid recipients" validation for single-select mode.
export function validateRecipientCount(
  list: MessageList | null,
  channel: MessageListChannel,
  liveEmailCount: number | null = null,
): boolean | string {
  if (!list) return true
  const count = recipientCountForList(list, channel, liveEmailCount)
  if (count !== null && count === 0) {
    return 'There are no valid, opted-in contacts in this list'
  }
  return true
}
